import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, FormControlLabel, Stack, Switch } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ThemeProvider } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import { CncService } from '../../api/services/cncService';
import {
  CncAwaitingDispatchItem,
  CncCustomerAtBayItem,
} from '../../types/api/response/cncresponse';
import {
  CLICK_AND_COLLECT_CNC_BOARD_ROUTE,
  CLICK_AND_COLLECT_CNC_BOARD_TV_ROUTE,
} from '../../constants/routes';
import { boardTheme } from '../Analytics/DispatchBoard/boardTheme';
import {
  Empty,
  MosaicPanel,
  Pill,
  PillSep,
  PlainRow,
  Rail,
  Scroller,
  StaleCage,
  useBoardCycle,
  usePagedRows,
  useRollingRange,
} from '../Analytics/DispatchBoard/MosaicChrome';

dayjs.extend(utc);
dayjs.extend(timezone);

const FIJI = 'Pacific/Fiji';

/**
 * The Click & Collect board (item 1081, §5.2.4.2).
 *
 * 🚨 IT DOES NOT READ LOCUS, which is why it works before `cnc` is switched on there. The gate,
 * the bay and the arrival live in SuperStore's own order database, served by the two CNC queue
 * endpoints the CNC Dashboard already uses. The Locus `skills = cnc` tag only decides whether a
 * DELIVERY row on the dispatch board wears a Click & Collect chip.
 *
 * ⚠️ The rider's leg is NOT here. A C&C order is an ordinary delivery to a collection point
 * (Justin, 14 Oct) and appears in Assigned and Active tour like any other stop. This board
 * covers only what happens after it arrives.
 */

/** Matches the CNC Dashboard, so the two surfaces can never disagree about a row. */
const QUERY_DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

/**
 * Slower than the dispatch board's two minutes, because nothing here is derived — these are
 * live queue reads, and a customer standing in a bay is worth seeing inside half a minute.
 */
const REFRESH_MS = 30 * 1000;

/** Same threshold as the dispatch board: the caption changes before the takeover does. */
const STALE_AFTER_MINUTES = 10;

const CONTROLS_IDLE_MS = 10 * 1000;

/** Remembered per browser, so a television comes back the way the room left it. */
const THEME_KEY = 'ss_cnc_daylight';

/**
 * ⚠️ 30 SECONDS, NOT 5 (§1.1): "nothing turns faster than 30 seconds... the first cut turned
 * every 5–10s and was unreadable across a room." Overridable with ?secs= for testing.
 */
const DEFAULT_ROTATE_SECONDS = 30;
const MIN_ROTATE_SECONDS = 2;
const MAX_ROTATE_SECONDS = 60;

/**
 * ⚠️ AT A BAY OUTRANKS AT THE GATE. Both are customers who have turned up, but one of them is
 * parked in a bay with nobody bringing the order out. §5.2.4.2 ranks bay first for that reason.
 */
const AT_BAY = 'CUSTOMER_AT_BAY';

const minutesSince = (iso: string | null): number | null => {
  if (!iso) return null;
  const then = dayjs(iso);
  return then.isValid() ? dayjs().diff(then, 'minute') : null;
};

/**
 * 🚨 FIJI, NOT THE VIEWER'S ZONE, like every other time on these boards — the rail's clock is
 * Fiji, so a slot in the browser's zone made a live window look hours expired beside it.
 *
 * ⚠️ This is why the slot will NOT match the CNC Dashboard from outside Fiji: that renders in
 * the viewer's zone, this one in the warehouse's.
 */
const slotLabel = (item: CncCustomerAtBayItem): string => {
  if (!item.delivery_slot) return '';
  const from = dayjs(item.delivery_slot.start_time);
  const to = dayjs(item.delivery_slot.end_time);
  if (!from.isValid() || !to.isValid()) return '';
  return `${from.tz(FIJI).format('HH:mm')}–${to.tz(FIJI).format('HH:mm')}`;
};

/** "Jane Doe · 6 items · 12:30–14:30" — every part dropped rather than printed empty. */
const describe = (customer: string, items: number, slot?: string): string =>
  [customer || '—', items ? `${items} ${items === 1 ? 'item' : 'items'}` : '', slot]
    .filter(Boolean)
    .join(' · ');

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export const CncBoardPage = ({ tv = false }: { tv?: boolean }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [arrived, setArrived] = useState<CncCustomerAtBayItem[]>([]);
  const [queued, setQueued] = useState<CncAwaitingDispatchItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [asOf, setAsOf] = useState<Dayjs | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  /**
   * Dark by default, remembered in the browser (Nikhil, 23 Oct). This supersedes the 06:00/18:00
   * auto-switch: a television restarts and must come back the way the room left it, and the
   * clock cannot know whether the roller door is open.
   *
   * ⚠️ Reads can throw — a private window, blocked site data — and a board that failed to open
   * because it could not remember a colour would be a poor trade.
   */
  const [daylight, setDaylight] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(THEME_KEY) === '1';
    } catch {
      return false;
    }
  });

  const toggleTheme = () => {
    setDaylight((on) => {
      const next = !on;
      try {
        window.localStorage.setItem(THEME_KEY, next ? '1' : '0');
      } catch {
        // Not remembering is survivable; refusing to switch is not.
      }
      return next;
    });
  };

  /**
   * ⚠️ THE SAME WINDOW THE CNC DASHBOARD USES — yesterday through tomorrow. A board that showed
   * a narrower range than the dashboard beside it would read as rows going missing, and a C&C
   * slot can sit on either side of midnight.
   */
  const [range, setRange] = useRollingRange(
    () => ({
      from: dayjs().subtract(1, 'day').startOf('day'),
      to: dayjs().add(1, 'day').startOf('day'),
    }),
    // ⚠️ The viewer's zone, NOT Fiji, unlike the dispatch board. The CNC queue endpoints take
    // naive datetimes and the CNC Dashboard sends them in local time; querying a different day
    // from the dashboard would make the two screens disagree about which rows exist.
    () => dayjs().format('YYYY-MM-DD'),
  );

  const load = useCallback(
    async (quiet = false) => {
      if (!quiet) setLoading(true);
      try {
        const [atBay, awaiting] = await Promise.all([
          // ⚠️ warehouse_id is left unset until there is more than one collection point to
          // choose between. Passing a guess would silently hide the other warehouses' queues.
          CncService.getCustomerAtBay(
            undefined,
            range.from.startOf('day').format(QUERY_DATE_FORMAT),
            range.to.endOf('day').format(QUERY_DATE_FORMAT),
          ),
          CncService.getAwaitingDispatch(
            undefined,
            range.from.startOf('day').format(QUERY_DATE_FORMAT),
            range.to.endOf('day').format(QUERY_DATE_FORMAT),
          ),
        ]);
        setArrived(atBay ?? []);
        setQueued(awaiting ?? []);
        setAsOf(dayjs());
      } catch (e: any) {
        // 🚨 SILENT ON THE WALL DISPLAY (Nikhil, 23 Oct). Nobody stands in front of a television
        // to read a toast, and a dropped poll does not mean the board is wrong — it still holds
        // the last good read, and the rail ages and then takes the screen over. The page keeps
        // the message, because someone is sitting there and can act on it.
        if (!tv) {
          dispatch(
            showNotification({
              message: e?.response?.data?.message ?? 'Failed to refresh the Click & Collect board.',
              severity: 'error',
            }),
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [dispatch, range, tv],
  );

  useEffect(() => {
    load();
    timer.current = setInterval(() => load(true), REFRESH_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [load]);

  /**
   * 🚨 A TELEVISION MUST NOT SLEEP — nobody touches it, so the browser's idle timer blanks it.
   * Released on unmount, and every failure swallowed: a board that refused to open because a
   * screen lock was denied would be worse than one that dims.
   */
  useEffect(() => {
    if (!tv) return undefined;
    let cancelled = false;
    let sentinel: any = null;

    const acquire = async () => {
      try {
        sentinel = await (navigator as any)?.wakeLock?.request?.('screen');
      } catch {
        sentinel = null;
      }
    };

    const onVisible = () => {
      if (!cancelled && document.visibilityState === 'visible') acquire();
    };

    acquire();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      sentinel?.release?.().catch(() => undefined);
    };
  }, [tv]);

  /** The document itself must not scroll on a wall display (§1.1). Restored on unmount. */
  useEffect(() => {
    if (!tv) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [tv]);

  /**
   * Remove the controls once nobody is there. Unmounted rather than faded: a faded row keeps
   * its height, and the board still overflows underneath it.
   */
  const [controlsIdle, setControlsIdle] = useState(false);

  useEffect(() => {
    if (!tv) return undefined;
    let idle: ReturnType<typeof setTimeout>;
    const wake = () => {
      setControlsIdle(false);
      clearTimeout(idle);
      idle = setTimeout(() => setControlsIdle(true), CONTROLS_IDLE_MS);
    };
    wake();
    window.addEventListener('mousemove', wake);
    window.addEventListener('keydown', wake);
    window.addEventListener('touchstart', wake);
    return () => {
      clearTimeout(idle);
      window.removeEventListener('mousemove', wake);
      window.removeEventListener('keydown', wake);
      window.removeEventListener('touchstart', wake);
    };
  }, [tv]);

  const [isFullscreen, setIsFullscreen] = useState(Boolean(document.fullscreenElement));

  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const goFullscreen = () => {
    document.documentElement.requestFullscreen?.().catch(() => undefined);
  };

  const enterTv = () => {
    navigate(CLICK_AND_COLLECT_CNC_BOARD_TV_ROUTE);
    goFullscreen();
  };

  const exitTv = () => {
    document.exitFullscreen?.().catch(() => undefined);
    navigate(CLICK_AND_COLLECT_CNC_BOARD_ROUTE);
  };

  const rotateSeconds = Math.min(
    MAX_ROTATE_SECONDS,
    Math.max(
      MIN_ROTATE_SECONDS,
      Number(new URLSearchParams(window.location.search).get('secs')) || DEFAULT_ROTATE_SECONDS,
    ),
  );
  // Staggered so the two panels do not flip on the same frame, as on the dispatch board.
  const board = useBoardCycle(rotateSeconds, tv);
  const queuedBeat = useBoardCycle(rotateSeconds, tv, rotateSeconds * 0.5);

  /**
   * ⚠️ BAY BEFORE GATE, LONGEST WAIT FIRST WITHIN EACH (§5.2.4.2). Sorting by arrival alone
   * would bury a customer parked in a bay behind three who have only just been admitted.
   */
  const arrivedSorted = [...arrived].sort((a, b) => {
    const rank = (r: CncCustomerAtBayItem) => (r.order_status === AT_BAY ? 0 : 1);
    if (rank(a) !== rank(b)) return rank(a) - rank(b);
    return (minutesSince(b.arrived_at) ?? 0) - (minutesSince(a.arrived_at) ?? 0);
  });

  /** Stalest first, for the same reason Waiting for a rider is (§5.9). */
  const queuedSorted = [...queued].sort(
    (a, b) => (minutesSince(b.created_date) ?? 0) - (minutesSince(a.created_date) ?? 0),
  );

  const arrivedPaged = usePagedRows(arrivedSorted, board.cycle, tv);
  const queuedPaged = usePagedRows(queuedSorted, queuedBeat.cycle, tv);

  const atBay = arrived.filter((r) => r.order_status === AT_BAY).length;
  const atGate = arrived.length - atBay;

  const ageMinutes = asOf ? dayjs().diff(asOf, 'minute') : null;
  const stale = ageMinutes !== null && ageMinutes >= STALE_AFTER_MINUTES;
  const asOfMoment = asOf ? asOf.tz(FIJI) : null;
  const asOfLabel = asOfMoment ? `${asOfMoment.format('HH:mm')} FJT` : 'unknown';
  const asOfFull = asOfMoment ? asOfMoment.format('ddd D MMM, HH:mm [Fiji]') : 'unknown';

  if (loading) {
    return (
      <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </div>
    );
  }

  const controls = (
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label="From"
          value={range.from}
          onChange={(v) => v && setRange((r) => ({ ...r, from: v.startOf('day') }))}
          maxDate={range.to}
          slotProps={{ textField: { size: 'small', sx: { width: 165 } } }}
        />
        <DatePicker
          label="To"
          value={range.to}
          onChange={(v) => v && setRange((r) => ({ ...r, to: v.startOf('day') }))}
          minDate={range.from}
          slotProps={{ textField: { size: 'small', sx: { width: 165 } } }}
        />
      </LocalizationProvider>
      <FormControlLabel
        sx={{ mr: 0 }}
        control={<Switch size="small" checked={daylight} onChange={toggleTheme} />}
        label="Daylight"
      />
      {!isFullscreen && (
        <Button size="small" variant="outlined" onClick={tv ? goFullscreen : enterTv}>
          Fullscreen
        </Button>
      )}
      {tv && (
        <Button size="small" variant="text" onClick={exitTv}>
          Exit
        </Button>
      )}
    </Stack>
  );

  return (
    <ThemeProvider theme={boardTheme(daylight, tv)}>
      <Box
        sx={{
          color: 'text.primary',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
          ...(tv ? { position: 'fixed', inset: 0, overflow: 'hidden' } : { minHeight: '70vh' }),
        }}
      >
        <Rail
          tv={tv}
          title="Click & Collect"
          feed="CLICK & COLLECT"
          dayLabel={`${range.from.format('ddd D MMM')} – ${range.to.format('ddd D MMM')}`}
          stale={stale}
          asOfLabel={asOfLabel}
          asOfFull={asOfFull}
          pills={
            <>
              <Pill
                n={atBay}
                label="at a bay"
                tone="warn"
                title="Customer is parked in a bay now"
              />
              <Pill n={atGate} label="at the gate" title="Admitted, not yet in a bay" />
              <PillSep />
              <Pill
                n={queued.length}
                label="awaiting dispatch"
                title="Packed and ready, not yet taken out to the point"
              />
            </>
          }
        />

        {!(tv && controlsIdle) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
              flex: '0 0 auto',
              px: 2,
              pb: 1.5,
            }}
          >
            {controls}
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'grid',
            gap: 2,
            px: 2,
            pb: 2,
            position: 'relative',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
            gridTemplateAreas: {
              xs: `"c-queue" "c-here"`,
              lg: `"c-queue c-here"`,
            },
            ...(tv && { gridTemplateRows: 'minmax(0, 1fr)' }),
          }}
        >
          <MosaicPanel
            id="c-here"
            tv={tv}
            title="At the collection point"
            sub="customer has arrived"
            meta={
              arrivedPaged.pages > 1
                ? `${arrived.length} waiting · page ${arrivedPaged.page + 1}/${arrivedPaged.pages}`
                : `${arrived.length} waiting`
            }
            beat={rotateSeconds}
            cycle={queuedBeat.cycle}
          >
            <Scroller tv={tv} ref={arrivedPaged.ref}>
              {arrivedSorted.length === 0 ? (
                <Empty>Nobody is waiting at the collection point.</Empty>
              ) : (
                arrivedPaged.shown.map((item) => (
                  <PlainRow
                    key={item.order_no}
                    kind={item.order_status === AT_BAY ? 'bay' : 'gate'}
                    tag={item.order_status === AT_BAY ? (item.bay?.label ?? 'bay') : 'gate'}
                    id={item.order_no}
                    what={describe(item.customer_name, item.item_count, slotLabel(item))}
                    ago={minutesSince(item.arrived_at)}
                  />
                ))
              )}
            </Scroller>
          </MosaicPanel>

          <MosaicPanel
            id="c-queue"
            tv={tv}
            title="Awaiting dispatch"
            sub="packed, not yet taken out"
            meta={
              queuedPaged.pages > 1
                ? `${plural(queued.length, 'order')} · page ${queuedPaged.page + 1}/${queuedPaged.pages}`
                : plural(queued.length, 'order')
            }
            beat={rotateSeconds}
            cycle={board.cycle}
          >
            <Scroller tv={tv} ref={queuedPaged.ref}>
              {queuedSorted.length === 0 ? (
                <Empty>Nothing packed and waiting to go out.</Empty>
              ) : (
                queuedPaged.shown.map((item) => (
                  <PlainRow
                    key={item.order_no}
                    id={item.order_no}
                    what={describe(item.customer_name, item.item_count)}
                    ago={minutesSince(item.created_date)}
                  />
                ))
              )}
            </Scroller>
          </MosaicPanel>

          {tv && stale && <StaleCage asOfLabel={asOfLabel} />}
        </Box>
      </Box>
    </ThemeProvider>
  );
};
